import type { Circuit } from '@multirx/shared/types/track.js';
import type { Driver } from '@multirx/shared/types/driver.js';
import type { RaceHistoryDetail, RaceHistorySummary } from '@multirx/shared/types/raceHistory.js';
import { mergeReplayCircuit, resolveReplayCircuit } from '@multirx/shared/game/archiveReplay.js';
import { fetchCircuitById } from '../circuits/api.js';
import {
  ARCHIVE_LIST_PAGE_SIZE,
  fetchRaceHistoriesPage,
  fetchRaceHistory,
} from '../raceHistory/api.js';
import {
  mountArchiveReplayBoard,
  type ArchiveReplayController,
} from '../raceHistory/ArchiveReplayBoard.js';
import { shouldFillViewportWithNextPage } from '../raceHistory/archivesFillViewport.js';
import { createPageEmptyState } from '../ui/pageEmptyState.js';
import { mountEarthLoaderOverlay } from '../ui/earthLoader.js';
import { divisionLabel, formatRaceDate, raceInvolvesDriver } from './accountProfileHelpers.js';

/** Pages API max par tick — évite un « Chargement… » bloqué à scanner tout l’historique. */
const MAX_API_PAGES_PER_TICK = 4;

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

async function resolveCircuitForReplay(detail: RaceHistoryDetail): Promise<Circuit | null> {
  const embedded = detail.replay.circuit;
  let fetched: Circuit | undefined;
  if (detail.circuitId != null) {
    try {
      fetched = (await fetchCircuitById(detail.circuitId)).circuit;
    } catch {
      fetched = undefined;
    }
  }
  return resolveReplayCircuit(embedded, fetched);
}

export type AccountProfileResultsHandle = {
  setDriver: (driver: Driver) => void;
  destroy: () => void;
};

/** Tableau résultats avec scroll infini et ligne expandable (replay inline). */
export function mountAccountProfileResultsTable(
  host: HTMLElement,
  initialDriver: Driver
): AccountProfileResultsHandle {
  host.innerHTML = '';
  host.classList.add('account-profile-results-table');

  let driver = initialDriver;
  let items: RaceHistorySummary[] = [];
  let apiOffset = 0;
  let hasMoreApi = true;
  let loading = false;
  let requestGen = 0;
  let expandedId: number | null = null;
  let replayController: ArchiveReplayController | null = null;
  let sentinelObserver: IntersectionObserver | null = null;
  let tbody: HTMLTableSectionElement | null = null;
  let dismissLoader: (() => void) | null = null;

  const status = document.createElement('p');
  status.className = 'account-profile-results__status muted';
  status.setAttribute('aria-live', 'polite');
  status.hidden = true;

  const emptyHost = document.createElement('div');
  emptyHost.className = 'account-profile-results__empty';
  emptyHost.hidden = true;

  const wrap = document.createElement('div');
  wrap.className = 'accounts-table-wrap account-profile-results__wrap';

  const sentinel = document.createElement('div');
  sentinel.className = 'account-profile-results__sentinel';
  sentinel.setAttribute('aria-hidden', 'true');

  host.append(status, emptyHost, wrap, sentinel);

  const destroyReplay = (): void => {
    replayController?.destroy();
    replayController = null;
  };

  const disconnectSentinel = (): void => {
    sentinelObserver?.disconnect();
    sentinelObserver = null;
  };

  const hideLoader = (): void => {
    dismissLoader?.();
    dismissLoader = null;
  };

  const showLoader = (): void => {
    if (dismissLoader) return;
    dismissLoader = mountEarthLoaderOverlay(host, 'Chargement des résultats…');
  };

  const showEmpty = (): void => {
    hideLoader();
    status.hidden = true;
    status.textContent = '';
    wrap.innerHTML = '';
    tbody = null;
    emptyHost.hidden = false;
    emptyHost.replaceChildren(
      createPageEmptyState({
        title: 'Aucune course pour l’instant',
        description:
          'Les résultats de votre pilote apparaîtront ici après vos courses (solo, championnat ou multi).',
        primaryAction: { label: 'Voir les replays', href: '#archives' },
      })
    );
  };

  const showError = (message: string): void => {
    hideLoader();
    emptyHost.hidden = true;
    emptyHost.innerHTML = '';
    wrap.innerHTML = '';
    tbody = null;
    status.hidden = false;
    status.classList.add('auth-error');
    status.textContent = message;
  };

  const updateStatus = (): void => {
    if (items.length === 0) {
      status.hidden = true;
      return;
    }
    emptyHost.hidden = true;
    emptyHost.innerHTML = '';
    status.hidden = false;
    status.classList.remove('auth-error');
    status.textContent = `${items.length} course${items.length > 1 ? 's' : ''}${
      hasMoreApi ? '…' : ''
    }`;
  };

  const ensureTable = (): HTMLTableSectionElement => {
    if (tbody && wrap.contains(tbody.closest('table'))) return tbody;
    wrap.innerHTML = '';
    const table = document.createElement('table');
    table.className = 'data-table accounts-table account-profile-races-table';
    table.innerHTML =
      '<thead><tr><th class="accounts-col-expand" aria-hidden="true"></th><th>Circuit</th><th>Division</th><th>Date</th><th>Résultat</th></tr></thead>';
    tbody = document.createElement('tbody');
    table.appendChild(tbody);
    wrap.appendChild(table);
    return tbody;
  };

  const clearExpandRows = (): void => {
    destroyReplay();
    tbody?.querySelectorAll('tr.accounts-detail-row').forEach((row) => row.remove());
    tbody?.querySelectorAll('tr.accounts-row--open').forEach((row) => {
      row.classList.remove('accounts-row--open');
      row.setAttribute('aria-expanded', 'false');
    });
  };

  const appendRaceRows = (races: RaceHistorySummary[]): void => {
    if (races.length === 0) return;
    emptyHost.hidden = true;
    emptyHost.innerHTML = '';
    const body = ensureTable();
    for (const race of races) {
      body.appendChild(
        renderSummaryRow(race, false, driver, () => {
          void toggleExpand(race.id);
        })
      );
    }
  };

  const toggleExpand = async (raceId: number): Promise<void> => {
    const body = ensureTable();
    if (expandedId === raceId) {
      expandedId = null;
      clearExpandRows();
      return;
    }

    clearExpandRows();
    expandedId = raceId;

    const summaryRow = [...body.querySelectorAll<HTMLTableRowElement>('tr.accounts-row')].find(
      (row) => Number(row.dataset.raceId) === raceId
    );
    if (summaryRow) {
      summaryRow.classList.add('accounts-row--open');
      summaryRow.setAttribute('aria-expanded', 'true');
    }

    const detailRow = document.createElement('tr');
    detailRow.className = 'accounts-detail-row';
    detailRow.dataset.raceId = String(raceId);
    const cell = document.createElement('td');
    cell.colSpan = 5;
    cell.className = 'accounts-detail-cell';
    cell.innerHTML =
      '<div class="account-profile-replay accounts-detail"><p class="muted">Chargement du replay…</p></div>';
    detailRow.appendChild(cell);
    if (summaryRow?.nextSibling) {
      body.insertBefore(detailRow, summaryRow.nextSibling);
    } else {
      body.appendChild(detailRow);
    }

    const gen = requestGen;
    const panel = cell.querySelector<HTMLElement>('.account-profile-replay');
    if (!panel) return;

    try {
      const detail = await fetchRaceHistory(raceId);
      if (gen !== requestGen || expandedId !== raceId) return;
      const circuit = await resolveCircuitForReplay(detail);
      if (gen !== requestGen || expandedId !== raceId) return;

      panel.innerHTML = '';
      const boardHost = document.createElement('div');
      boardHost.className = 'archives-replay-board-host account-profile-replay__board';
      panel.appendChild(boardHost);

      if (!circuit?.path?.length) {
        boardHost.innerHTML =
          '<p class="archives-replay-board-fallback">Circuit introuvable — replay visuel indisponible.</p>';
        return;
      }

      const replayForView = mergeReplayCircuit(detail.replay, circuit);
      destroyReplay();
      replayController = mountArchiveReplayBoard(
        boardHost,
        circuit,
        replayForView,
        detail.numParticipants,
        1,
        detail.circuitName
      );
    } catch (err) {
      if (gen !== requestGen || expandedId !== raceId) return;
      panel.innerHTML = `<p class="auth-error">${escapeHtml(err instanceof Error ? err.message : 'Erreur replay')}</p>`;
    }
  };

  const loadMore = async (): Promise<void> => {
    if (loading || !hasMoreApi) return;
    loading = true;
    const gen = requestGen;
    const initialLoad = items.length === 0;
    if (initialLoad) showLoader();

    try {
      let pagesFetched = 0;
      const batch: RaceHistorySummary[] = [];
      while (
        hasMoreApi &&
        batch.length < ARCHIVE_LIST_PAGE_SIZE &&
        pagesFetched < MAX_API_PAGES_PER_TICK
      ) {
        pagesFetched += 1;
        const page = await fetchRaceHistoriesPage(ARCHIVE_LIST_PAGE_SIZE, apiOffset);
        if (gen !== requestGen) return;
        apiOffset += page.items.length;
        hasMoreApi = page.hasMore && page.items.length > 0;
        const mine = page.items.filter((r) => raceInvolvesDriver(r, driver));
        if (mine.length > 0) batch.push(...mine);
        if (page.items.length === 0) {
          hasMoreApi = false;
          break;
        }
      }
      if (gen !== requestGen) return;

      if (batch.length > 0) {
        items = items.concat(batch);
        hideLoader();
        appendRaceRows(batch);
        updateStatus();
      } else if (items.length === 0 && !hasMoreApi) {
        showEmpty();
      } else if (items.length === 0 && hasMoreApi) {
        // Encore des pages API à parcourir, mais pas de match dans ce tick — on laisse le loader.
        updateStatus();
      } else {
        hideLoader();
        updateStatus();
      }
    } catch (err) {
      if (gen !== requestGen) return;
      showError(err instanceof Error ? err.message : 'Impossible de charger les résultats.');
      hasMoreApi = false;
    } finally {
      loading = false;
    }

    if (gen !== requestGen) return;

    if (items.length > 0) {
      hideLoader();
    }
    disconnectSentinel();
    wireSentinel();
    // Enchaîne si le viewport / le filtre sparse le demandent.
    if (
      shouldFillViewportWithNextPage({
        loading: false,
        hasMore: hasMoreApi,
        sentinelInViewport: sentinel.getBoundingClientRect().top < window.innerHeight + 240,
      }) ||
      (items.length === 0 && hasMoreApi)
    ) {
      void loadMore();
    }
  };

  const wireSentinel = (): void => {
    disconnectSentinel();
    if (!hasMoreApi) return;
    sentinelObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { root: null, rootMargin: '240px' }
    );
    sentinelObserver.observe(sentinel);
  };

  const resetAndLoad = (): void => {
    requestGen += 1;
    items = [];
    apiOffset = 0;
    hasMoreApi = true;
    loading = false;
    expandedId = null;
    destroyReplay();
    hideLoader();
    wrap.innerHTML = '';
    tbody = null;
    emptyHost.hidden = true;
    emptyHost.innerHTML = '';
    status.hidden = true;
    status.textContent = '';
    status.classList.remove('auth-error');
    void loadMore();
  };

  resetAndLoad();

  return {
    setDriver(next: Driver): void {
      const renamed = next.firstName !== driver.firstName || next.lastName !== driver.lastName;
      driver = next;
      if (renamed) resetAndLoad();
    },
    destroy(): void {
      requestGen += 1;
      loading = false;
      destroyReplay();
      disconnectSentinel();
      hideLoader();
      host.innerHTML = '';
    },
  };
}

function renderSummaryRow(
  race: RaceHistorySummary,
  open: boolean,
  driver: Driver,
  onToggle: () => void
): HTMLTableRowElement {
  const row = document.createElement('tr');
  row.className = `accounts-row${open ? ' accounts-row--open' : ''}`;
  row.dataset.raceId = String(race.id);
  row.tabIndex = 0;
  row.setAttribute('aria-expanded', open ? 'true' : 'false');

  const full = `${driver.firstName} ${driver.lastName}`.trim().toLowerCase();
  const won = race.winnerDriverName?.trim().toLowerCase() === full;
  const circuit = race.circuitName?.trim() || `Circuit #${race.circuitId ?? '?'}`;

  row.innerHTML = `
    <td class="accounts-col-expand"><span class="accounts-expand-icon" aria-hidden="true"></span></td>
    <td>${escapeHtml(circuit)}</td>
    <td>${escapeHtml(divisionLabel(race.division))}</td>
    <td>${escapeHtml(formatRaceDate(race.createdAt) || '—')}</td>
    <td>${won ? 'Victoire' : '—'}</td>
  `;

  row.addEventListener('click', onToggle);
  row.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  });
  return row;
}
